import axios from "axios";
import swal from "sweetalert";
import { v4 as uuid } from "uuid";
import path from "path";
import moment from "moment";

import { _CFG } from "../config";
import _CK from "./cookie";


const api = {
  apiKey: _CFG.apiKey,
  apiHost: _CFG.apiHost(),
};

api.normalizeError = (error) => {
  if (error instanceof Error) {
    return error;
  }

  const message =
    typeof error === "string"
      ? error
      : error?.message ||
        error?.msg ||
        (error?.error !== undefined ? `API Error (${error.error})` : "알 수 없는 오류가 발생했습니다.");

  const normalizedError = new Error(message);

  if (error && typeof error === "object") {
    Object.keys(error).forEach((key) => {
      normalizedError[key] = error[key];
    });
    normalizedError.raw = error;
  }

  return normalizedError;
};

api.get = (request) => {
  request.method = "GET";
  return api.send(request);
};
api.post = (request) => {
  request.method = "POST";
  return api.send(request);
};
api.put = (request) => {
  request.method = "PUT";
  return api.send(request);
};
api.patch = (request) => {
  request.method = "PATCH";
  return api.send(request);
};
api.delete = (request) => {
  request.method = "DELETE";
  return api.send(request);
};

api.send = (request) => {
  const { path, data } = request;
  let { auth, method, retry, isNoErrorNotify, ondemand } = request;
  const requestHeaders = request.headers || {};
  if (!auth) {
    // 정상 브랜치(campaign_Event_sync_pannel)와 동일: snapshot(hasConsoleSession) 게이트 없이
    // 쿠키 auth 를 그대로 전송한다. 실제 유효성은 서버가 판정.
    // (snapshot 불일치 시 모든 API가 auth="" 로 호출돼 관리자 F5에서 console/info 등이 실패하던 문제 해결)
    auth = _CK.getAuth() || "";
  }
  if (!method) method = "GET";
  if (retry === null || retry === undefined) retry = 5;
  if (!isNoErrorNotify) isNoErrorNotify = false;

  return new Promise((resolve, reject) => {
    const req = {
      method,
      url: ondemand ? path : `${api.apiHost}${path}`,
      headers: {
        apikey: api.apiKey,
        authorization: auth,
        ...requestHeaders,
      },
      data: ["POST", "PUT", "PATCH"].includes(method.toUpperCase()) ? data : null,
      params: method.toUpperCase() === "GET" ? data : null,
    };
    
    
    axios(req)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error(res.statusText);
        }

        const { data } = res;
        if (data.error !== 0) {
          throw api.normalizeError(data);
        }

        resolve && resolve(data);
      })
      .catch((e) => {
        const error = api.normalizeError(e);

        if (error.message === "Network Error" && retry > 0) {
          request.retry = retry - 1;
          setTimeout(() => {
            api.send(request).then(resolve).catch(reject);
          }, 500);
          return;
        }

        if (!isNoErrorNotify) {
          api.error(error, reject);
        } else {
          reject && reject(error);
        }
      });
  });
};

api.iam = (auth, eventIdx, options = {}) => {
  const startTime = performance.now();
  const { isNoErrorNotify = false } = options;
  
 
  const req = {
    path: "/v1/client/iam",
    method: "POST",
    auth: auth ? auth : _CK.getAuth(),
    isNoErrorNotify,
    data:{
      logKey:_CK.getLogKey(),
      consoleCountView:_CK.getConsoleCountView(),
      ...(eventIdx && { eventIdx })  // ✅ URL에서 추출한 eventIdx가 있으면 포함
    }
  };
  
  return api.send(req).then((result) => {
    const elapsed = performance.now() - startTime;
    
   
    return result;
  }).catch((error) => {
    const elapsed = performance.now() - startTime;
    
    console.error('%c❌ [IAM Error - Frontend]', 'color: #e84118; font-weight: bold', {
      elapsed: `${elapsed.toFixed(2)}ms`,
      error: error.message || error,
    });
    console.log('%c========================================', 'color: #00a8ff; font-weight: bold');
    
    throw error;
  });
};

api.prereg = (preregIdx, auth) => {
  const req = {
    path: "/v1/client/prereg/info",
    method: "POST",
    auth: auth ? auth : _CK.getAuth(),
    data: { preregIdx: preregIdx },
  };
  return api.send(req);
};

api.loadUploadKeys = () => {
  const req = {
    path: "/v1/upload",
    method: "GET",
  };
  return api.send(req);
};

api.error = (e, reject) => {
  let message = e.message;

  if (e.error) {
    switch (e.error) {
      case -2:
        message = "API Key가 누락되었습니다.";
        break;
      case -3:
        message = "API Key를 찾을 수 없습니다.";
        break;
      case -4:
        message = "인증정보가 누락되었습니다.";
        break;
      case -5:
        message = "인증정보를 확인 할 수 없습니다.";
        break;
      case -12:
        message = "아이디를 입력해 주세요.";
        break;
      case -13:
        message = "비밀번호를 입력해 주세요.";
        break;
      case -14:
        message = "참가 신청 하지 않은 이벤트입니다.";
        break;
      case -15:
        message = "회원상태가 올바르지 않습니다. 관리자와 상담해 주세요.";
        break;
    }
  }

  if(e.error == -99){
    let el = document.createElement('div');
    el.innerHTML = message;
    swal({ title: "Notice", content: el }).then(() => {
      reject && reject(e);
    });
  }else{

    if(message == 'Network Error') {
      reject && reject(e);
      return false;
    }
    
    swal({ title: "Notice", text: message }).then(() => {
      reject && reject(e);
    });
  }

  
};

api.upload = async ({ aws, file, security, path: location, type }) => {
  if (!aws) throw new Error("AWS 정보를 확인해 주세요.");
  if (!file) throw new Error("파일을 입력해 주세요.");
  const isLocal = _CFG.isLocal();

  // 1. presigned url 요청
  const fileName = file.name;
  const fileType = file.type;
  const isPrivate = !!security;
  const presignRes = await api.send({
    path: "/v1/upload/presign",
    method: "POST",
    data: { fileName, fileType, isPrivate },
  });
  const { url, key, bucket } = presignRes;

  // 2. presigned url로 파일 업로드
  await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": fileType,
      // 'x-amz-acl': 'private', // 필요시
    },
    body: file,
  });

  // 3. 업로드 정보 서버에 등록
  const uploaded = {
    bucket,
    path: key,
    name: file.name,
    size: file.size,
    type: file.type,
  };
  if (type) {
    uploaded.targetType = type;
  }
  const req = { method: "POST", path: "/v1/upload", data: uploaded };
  const res = await api.send(req);
  return res;
};

export default api;
